<template>
  <div class="init-wheel" v-if="segments.length">
    <svg class="wheel-svg" viewBox="0 0 120 120" aria-hidden="true">
      <g v-for="seg in segments" :key="seg.date" @click.stop="$emit('select-date', seg.date)">
        <title>{{ seg.date }} {{ seg.state }}</title>
        <path :d="seg.d" :fill="seg.fill" :class="['slice', 'is-' + seg.state, { 'is-active': seg.active }]" />
        <text class="slice-label" :x="seg.labelX" :y="seg.labelY" :fill="seg.labelFill" text-anchor="middle"
          dominant-baseline="central">{{ seg.index }}</text>
      </g>
      <circle class="hub" cx="60" cy="60" r="30" />
    </svg>
    <div class="wheel-meta">
      <div class="wheel-title" :class="{ 'is-idle': !working }">{{ titleText }}</div>
      <div class="wheel-sub">{{ subText }}</div>
    </div>
  </div>
</template>

<script>
const CX = 60;
const CY = 60;
const R0 = 32;
const R1 = 56;
const R_LABEL = (R0 + R1) / 2;
const GAP = 1.6;

const DARK_STATES = {
  queued: true,
  running: true,
  done: true,
  failed: true
};

const FILL = {
  idle: "#c5d8f0",
  pending: "#d6e9ff",
  paused: "#fdebd0",
  queued: "#5b9bd5",
  running: "#1b4fbf",
  done: "#67c23a",
  failed: "#f56c6c"
};

function polar(r, angleDeg) {
  const a = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: CX + r * Math.cos(a),
    y: CY + r * Math.sin(a)
  };
}

function donutSlice(start, end) {
  const span = end - start;
  const large = span > 180 ? 1 : 0;
  const p0 = polar(R1, start);
  const p1 = polar(R1, end);
  const p2 = polar(R0, end);
  const p3 = polar(R0, start);
  return [
    "M", p0.x, p0.y,
    "A", R1, R1, 0, large, 1, p1.x, p1.y,
    "L", p2.x, p2.y,
    "A", R0, R0, 0, large, 0, p3.x, p3.y,
    "Z"
  ].join(" ");
}

function shortMd(dateStr) {
  const m = String(dateStr || "").match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!m) {
    return dateStr || "-";
  }
  return m[2] + "-" + m[3];
}

export default {
  name: "EvInitDayWheel",
  props: {
    items: {
      type: Array,
      default() {
        return [];
      }
    },
    activeDate: {
      type: String,
      default: ""
    }
  },
  computed: {
    segments() {
      const rows = this.items || [];
      const n = rows.length;
      if (!n) {
        return [];
      }
      const step = 360 / n;
      const active = String(this.activeDate || "");
      return rows.map((row, i) => {
        const date = row && row.date ? String(row.date) : "";
        const state = this.resolveState(row);
        const start = i * step + GAP / 2;
        const end = (i + 1) * step - GAP / 2;
        const mid = polar(R_LABEL, (start + end) / 2);
        return {
          index: i + 1,
          date,
          state,
          active: !!date && date === active,
          fill: FILL[state] || FILL.idle,
          labelFill: DARK_STATES[state] ? "#fff" : "#1b4fbf",
          labelX: mid.x,
          labelY: mid.y,
          d: donutSlice(start, end)
        };
      });
    },
    runningDates() {
      return this.segments.filter((s) => s.state === "running").map((s) => s.date);
    },
    queuedDates() {
      return this.segments.filter((s) => s.state === "queued").map((s) => s.date);
    },
    working() {
      return this.runningDates.length > 0 || this.queuedDates.length > 0;
    },
    hubDate() {
      if (this.runningDates.length) {
        return shortMd(this.runningDates[0]);
      }
      if (this.queuedDates.length) {
        return "排队";
      }
      if (this.segments.some((s) => s.state === "paused" || s.state === "pending")) {
        return "暂停";
      }
      return "空闲";
    },
    hubSub() {
      const run = this.runningDates.length;
      const q = this.queuedDates.length;
      if (run || q) {
        return (run ? "解" + run : "") + (run && q ? " " : "") + (q ? "排" + q : "");
      }
      return this.segments.length + "天";
    },
    titleText() {
      if (this.runningDates.length) {
        return "正在求解 " + this.runningDates.map(shortMd).join("、");
      }
      if (this.queuedDates.length) {
        return "排队中 " + this.queuedDates.map(shortMd).join("、");
      }
      if (this.segments.some((s) => s.state === "paused")) {
        return "初始化暂停在线求解";
      }
      if (this.segments.some((s) => s.state === "pending")) {
        return "初始化中";
      }
      return "求解器空闲";
    },
    subText() {
      const run = this.runningDates.length;
      const q = this.queuedDates.length;
      if (run || q) {
        return "搜索 " + run + " 天 · 排队 " + q + " 天";
      }
      return "窗口 " + this.segments.length + " 天";
    }
  },
  methods: {
    resolveState(row) {
      if (!row) {
        return "idle";
      }
      if (row.initInFlight || row.solverAlive || row.state === "running") {
        return "running";
      }
      if (row.queued || row.state === "queued") {
        return "queued";
      }
      return row.state || "idle";
    }
  }
};
</script>

<style lang="less" scoped>
.init-wheel {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.wheel-svg {
  width: 55px;
  height: 55px;
  flex-shrink: 0;
}

.slice-label {
  font-size: 16px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  pointer-events: none;
  user-select: none;
}

.slice {
  stroke: #fff;
  stroke-width: 0.8;
  transition: fill 0.25s ease, stroke 0.25s ease;
  cursor: pointer;

  &.is-running {
    filter: drop-shadow(0 0 3px rgba(27, 79, 191, 0.55));
    animation: slice-pulse 1.6s ease-in-out infinite;
  }

  &.is-queued {
    filter: drop-shadow(0 0 2px rgba(91, 155, 213, 0.45));
  }

  &.is-active {
    stroke: #1b4fbf;
    stroke-width: 1.8;
  }
}

@keyframes slice-pulse {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.78;
  }
}

.hub {
  fill: #fff;
}

.wheel-meta {
  min-width: 0;
}

.wheel-title {
  font-size: 12px;
  font-weight: 600;
  color: #1b4fbf;
  line-height: 1.25;
  white-space: nowrap;

  &.is-idle {
    color: #909399;
    font-weight: 500;
  }
}

.wheel-sub {
  margin-top: 0;
  font-size: 11px;
  line-height: 1.25;
  color: #606266;
  font-variant-numeric: tabular-nums;
}
</style>
